import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Star } from "lucide-react";
import { PendingReviews } from "@/components/admin/PendingReviews";
import { ReviewPhotoPicker } from "@/components/review/ReviewPhotoPicker";
import { ReviewPhotoStrip } from "@/components/review/ReviewPhotoStrip";

const SOURCES = ["Google", "Yelp", "Text message", "Email", "Website", "Other"];
const EMPTY = { name: "", where: "", quote: "", stars: 5, source: "Google" };
export const QUOTE_MAX = 2000;
const NAME_MAX = 80;

const errorText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" · ") || fallback;
  return err?.response ? fallback : "Can't reach the server — check your connection and try again.";
};

export function AdminReviewsWall({ reviews, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const tooLong = form.quote.length > QUOTE_MAX;
  const live = reviews.filter((r) => r.status !== "pending");

  const add = async (e) => {
    e.preventDefault();
    if (tooLong) { toast.error(`Review text is too long — trim it under ${QUOTE_MAX} characters.`); return; }
    setBusy(true);
    const fd = new FormData(); Object.entries(form).forEach(([k, v]) => fd.append(k, v)); photos.forEach((p) => fd.append("photos", p));
    try { await api.post("/admin/reviews", fd); toast.success("Review added to the home page"); setForm({ ...EMPTY, source: form.source }); setPhotos([]); onChanged(); }
    catch (err) { toast.error(errorText(err, "Could not add review")); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Review removed"); onChanged(); }
    catch (err) { toast.error(errorText(err, "Could not remove review")); }
  };

  return (
    <div className="space-y-8" data-testid="admin-reviews-wall">
      <PendingReviews reviews={reviews} onChanged={onChanged} />
      <div className="text-xs text-slate-500">Customers can leave reviews themselves at <span className="font-mono text-slate-300">/review</span> (linked from the home page, footer and receipt emails). They stay hidden until you approve them here.</div>
      <form onSubmit={add} className="dg-card rounded-xl p-5 grid gap-4 sm:grid-cols-2" data-testid="review-form">
        <div className="sm:col-span-2 dg-eyebrow flex items-center gap-2"><Star className="w-3 h-3" /> Add a customer review (Google, Yelp, a text — anything they said)</div>
        <label className="block"><div className="dg-eyebrow mb-1">Customer name</div>
          <input className="dg-input" value={form.name} maxLength={NAME_MAX} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Marina S." required data-testid="review-name-input" /></label>
        <label className="block"><div className="dg-eyebrow mb-1">City or note (optional)</div>
          <input className="dg-input" value={form.where} maxLength={NAME_MAX} onChange={(e) => setForm({ ...form, where: e.target.value })} placeholder="e.g. Brentwood · Wi-Fi fix" data-testid="review-where-input" /></label>
        <label className="block sm:col-span-2">
          <div className="dg-eyebrow mb-1 flex items-center justify-between"><span>What they said</span>
            <span className={`font-mono normal-case tracking-normal ${tooLong ? "text-red-400" : "text-slate-500"}`} data-testid="review-quote-count">{form.quote.length} / {QUOTE_MAX}</span></div>
          <textarea className={`dg-input min-h-[120px] ${tooLong ? "border-red-500/60" : ""}`} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="Paste their words exactly" required data-testid="review-quote-input" /></label>
        <label className="block"><div className="dg-eyebrow mb-1">Stars</div>
          <select className="dg-input" value={form.stars} onChange={(e) => setForm({ ...form, stars: Number(e.target.value) })} data-testid="review-stars-select">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{"★".repeat(n)}</option>)}
          </select></label>
        <label className="block"><div className="dg-eyebrow mb-1">Where it came from</div>
          <select className="dg-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="review-source-select">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
        <div className="sm:col-span-2"><ReviewPhotoPicker photos={photos} setPhotos={setPhotos} prefix="admin-review" /></div>
        <div className="sm:col-span-2"><button type="submit" disabled={busy} className="dg-btn-primary rounded-full px-5 py-2.5 min-h-[44px] text-sm w-full sm:w-auto" data-testid="review-add-btn">{busy ? "Adding…" : "Add review"}</button></div>
      </form>

      {live.length === 0 ? <div className="text-slate-500 text-sm">No reviews yet — the home page shows sample quotes until you add real ones.</div> : (
        <div className="grid gap-3 md:grid-cols-2">
          {live.map((r) => (
            <div key={r.id} className="dg-card rounded-xl p-5" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-amber-400 text-sm font-mono">{"★".repeat(r.stars)}</div>
                  <p className="text-slate-300 text-sm mt-2 whitespace-pre-wrap break-words">"{r.quote}"</p>
                  <div className="mt-3 text-xs text-slate-500 font-mono uppercase tracking-widest">{r.name}{r.where ? ` · ${r.where}` : ""} · {r.source}</div>
                  <div className="mt-3"><ReviewPhotoStrip id={r.id} photos={r.photos} /></div>
                </div>
                <button onClick={() => remove(r.id)} className="dg-btn-ghost rounded-lg w-11 h-11 grid place-items-center shrink-0" aria-label="Remove review" data-testid={`review-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
